import {
  EMAIL_FILE_TYPES,
  IMAGE_FILE_TYPES,
  MS_FILE_TYPES,
  PDF_FILE_TYPES,
} from "../../utils/constants";
import { addNotification } from "../../slice/SnackbarSlice";
import { appendAIPaneTo } from "../../utils/aiPaneUrl";

export const onSelect = ({selectedKeys, info, dispatch, postHighlightedMatter, postLastPageViewed, fetchMatterDetail, location, setSelectedKeys, setTabClick, setUpdateMeta, setUniqueId, navigate, checkTabLimit, openTab, setActiveTab}) => {
    const fileType = info?.node?.record?.file_type;

    // Ctrl/Cmd/Shift-click builds a multi-selection (documents only) for
    // bulk copy and bulk drag-move (user request, 2026-07-22). Selection
    // clicks never open documents or navigate — a plain click still does.
    const native = info?.nativeEvent;
    if (native && (native.ctrlKey || native.metaKey || native.shiftKey)) {
      const documentKeys = (selectedKeys || []).filter((key) =>
        String(key).startsWith('D')
      );
      dispatch(setSelectedKeys(documentKeys));
      return;
    }
    
    // Prevent navigation if clicking on the same item that's already selected
    // Check if current location matches the target relativePath
    if (info.node?.relativePath) {
      const currentPath = location.pathname;
      const targetPath = `/matter${info.node.relativePath}`;
      
      // If already on the same page, don't navigate or perform any actions
      if (currentPath === targetPath) {
        return;
      }
    }
    
    // EML/MSG open through the server-rendered email preview; other formats
    // continue through their existing viewer paths.
    if (info.node?.type === 'document' && fileType) {
      const isSupported = PDF_FILE_TYPES.includes(fileType)
        || MS_FILE_TYPES.includes(fileType)
        || IMAGE_FILE_TYPES.includes(fileType)
        || EMAIL_FILE_TYPES.includes(fileType);
      if (!isSupported) {
        // Show error toast for unsupported file formats
        dispatch(addNotification({
          id: Date.now().toString(),
          newMsg: 'This file format is not supported in DMS at the moment.',
          success: false,
        }));
        return; // Prevent opening unsupported files
      }
    }
    
    // Check tab limit FIRST for documents (before any API calls, navigation, or state changes)
    // Pass the document's unique_id to check if it's already open
    if (info.node?.type === 'document' || PDF_FILE_TYPES.includes(fileType) || MS_FILE_TYPES.includes(fileType) || IMAGE_FILE_TYPES.includes(fileType)) {
      const documentUniqueId = info.node?.record?.unique_id;
      if (checkTabLimit && checkTabLimit(documentUniqueId)) {
        return; // Prevent ALL subsequent code execution (API calls, navigation, state changes)
      }
      
      // For documents, open tab immediately for instant feedback (before API calls and navigation)
      if (openTab && setActiveTab && info.node?.record) {
        const pdfRecord = {
          type: "document",
          record: info.node.record,
          file_type: info.node.record.file_type,
          unique_id: info.node.record.unique_id,
          name: info.node.record.name,
          ...info.node.record,
        };
        // Open tab immediately (before API calls and navigation)
        openTab(pdfRecord, false);
        // Set as active tab immediately for instant feedback
        setActiveTab(info.node.record.unique_id.toString());
      }
    }
    
    if(info.node?.type === 'folder' || PDF_FILE_TYPES.includes(fileType) || MS_FILE_TYPES.includes(fileType) || IMAGE_FILE_TYPES.includes(fileType))    {
      if(localStorage.getItem('lastPageViewedDoc')){
         const page_number = localStorage.getItem(`lastPageViewed`)
         const documentId = localStorage.getItem('lastPageViewedDoc')
          localStorage.removeItem(`lastPageViewed`)
          localStorage.removeItem(`lastPageViewedDoc`)
          dispatch(postLastPageViewed({docId : documentId, page_number}))
      }
      
      // For folders, ensure we navigate to list tab (tab "1")
      if (info.node?.type === 'folder' && setActiveTab) {
        setActiveTab("1");
        // Save relativePath to localStorage for list tab
        if (info.node.relativePath) {
          localStorage.setItem("relativePath", info.node.relativePath);
        }
      }
      
      // Navigate immediately (don't wait for API calls)
      // Preserve AI-pane state across the doc/folder switch so the panel
      // stays on the same tab the user opened it on. Legacy
      // `?chatbox=true` URLs are translated to `?ai=chat` by the helper.
      const targetPath = appendAIPaneTo(
        `/matter${info.node.relativePath}`,
        location.search
      );
      navigate(targetPath);
      
      // Do API calls in background (don't wait for them)
      const docType = info.node?.record.unique_id.startsWith('D') ? 'document' : 'folder';
      const highlightPayload = { 
        matterId: info.node?.record.matter, 
        docId: info.node?.record.unique_id.substring(1), 
        docType: docType 
      };
      
      // Add tab_action for documents only
      if (docType === 'document') {
        const userId = Number(localStorage.getItem("user_id"));
        highlightPayload.tabAction = {
          usr_id: userId,
          document_id: String(info.node?.record.unique_id.substring(1)),
          matter_id: String(info.node?.record.matter),
          action: "open"
        };
      }
      
      dispatch(postHighlightedMatter(highlightPayload)).then((res)=>{
        // Commented out fetchMatterDetail API call - not needed when selecting folder/document from side menu
      }).catch((error) => {
        console.error('Failed to post highlighted matter:', error);
      });
    }
    
    if (selectedKeys?.length > 0 || info.node?.type === 'folder') {
      dispatch(setSelectedKeys(selectedKeys));
    }

    dispatch(setTabClick(false));

    if (info.node?.type === 'folder') {
      setUniqueId(selectedKeys[0]);
    } else {
      dispatch(setUpdateMeta({ ...info.node?.record, type: info.node?.type }));
    }
    if(selectedKeys.length===0){
      // Preserve AI-pane state across the matter-root navigation.
      const targetPath = appendAIPaneTo(
        `/matter/${info.node.record.matter}`,
        location.search
      );
      navigate(targetPath);
    }
  }

export const onExpand = (keys, isSearching, setSearchExpandKeys, dispatch, setExpandedKeys) => {
    if (isSearching) {
      setSearchExpandKeys(keys); // allow interaction during search
    } else {
      dispatch(setExpandedKeys(keys));
    }
  };

export const handleTitleClick = (key, isSearching, searchExpandKeys, expandedKeys, setSearchExpandKeys, dispatch, setExpandedKeys) => {
    const keys = isSearching ? searchExpandKeys : expandedKeys;
    const newExpandedKeys = keys.includes(key)
      ? keys.filter((item) => item !== key)
      : [...keys, key];

    onExpand(newExpandedKeys, isSearching, setSearchExpandKeys, dispatch, setExpandedKeys);
  };